/**
 * Cell Batch Detail Page
 * Shows batch details, status history, and allows state machine transitions.
 * Handles version field for optimistic locking.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'wouter';
import { useCellBatchDetail, useCellBatchTransition, type CellBatchView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { canReadCellBatch, canUpdateCellBatch } from '@/lib/labPermissions';

type CellBatchStatus = 'planned' | 'sample_collected' | 'sample_received' | 'processing' | 'qc_pending' | 'released' | 'infusion_scheduled' | 'infused' | 'archived' | 'exception';

// Valid state transitions per state-machines.md
const VALID_TRANSITIONS: Record<string, string[]> = {
  planned: ['sample_collected', 'cancelled', 'exception_hold'],
  sample_collected: ['sample_received', 'cancelled', 'exception_hold'],
  sample_received: ['processing', 'cancelled', 'exception_hold'],
  processing: ['qc_pending', 'cancelled', 'exception_hold'],
  qc_pending: ['released', 'qc_failed', 'exception_hold'],
  qc_failed: ['cancelled', 'exception_hold'],
  released: ['infusion_scheduled', 'archived', 'exception_hold'],
  infusion_scheduled: ['infused', 'cancelled', 'exception_hold'],
  infused: ['archived'],
  exception_hold: ['processing', 'qc_pending', 'cancelled', 'archived'],
  archived: [],
  cancelled: [],
};

export default function CellBatchDetailPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const { permissions } = useAuth();
  const { fetch: fetchDetail, loading: detailLoading } = useCellBatchDetail(batchId!);
  const { transition: transitionStatus, loading: transitionLoading } = useCellBatchTransition(batchId);

  const [batch, setBatch] = useState<CellBatchView | null>(null);
  const [selectedTransition, setSelectedTransition] = useState<CellBatchStatus | ''> ('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadCellBatch(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看批次追溯的权限</p>
        </Card>
      </div>
    );
  }

  // Load batch detail
  useEffect(() => {
    const loadBatch = async () => {
      try {
        const data = await fetchDetail();
        if (data) {
          setBatch(data);
        }
      } catch (err) {
        toast.error('加载批次详情失败');
      }
    };

    if (batchId) {
      loadBatch();
    }
  }, [batchId, fetchDetail]);

  const handleTransition = async () => {
    if (!batch || !selectedTransition) {
      toast.error('请选择转换状态');
      return;
    }

    if (!canUpdateCellBatch(permissions)) {
      toast.error('您没有修改批次状态的权限');
      return;
    }

    try {
      // Type is narrowed by the check above
      await transitionStatus(selectedTransition, batch.version);
      toast.success('状态已更新');
      setSelectedTransition('');
      setConfirmDialogOpen(false);
      
      // Refetch batch details to ensure consistency with backend
      const updatedData = await fetchDetail();
      if (updatedData) {
        setBatch(updatedData);
      }
    } catch (err: unknown) {
      // Backend rejection must be shown as-is
      toast.error(`状态转换失败: ${(err as Error)?.message || '未知错误'}`);
    }
  };

  if (detailLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-muted-foreground">加载中…</div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">批次不存在</h2>
        </Card>
      </div>
    );
  }

  const validTransitions = VALID_TRANSITIONS[batch.status] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{batch.batchNo}</h1>
        <p className="text-sm text-muted-foreground">批次 ID: {batch.id}</p>
      </div>

      {/* Batch Info */}
      <Card className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-semibold text-muted-foreground">客户 ID</label>
            <p className="mt-1 text-sm">{batch.customerId}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">患者身份证 Hash</label>
            <p className="mt-1 text-sm font-mono">{batch.patientIdCardHash || 'N/A'}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">当前状态</label>
            <p className="mt-1 text-sm">
              <span className="inline-block rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-900">
                {getStatusLabel(batch.status)}
              </span>
            </p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">版本号</label>
            <p className="mt-1 text-sm font-mono">{batch.version}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">创建时间</label>
            <p className="mt-1 text-sm">{new Date(batch.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <label className="text-sm font-semibold text-muted-foreground">最后更新</label>
            <p className="mt-1 text-sm">{new Date(batch.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </Card>



      {/* Status Transition */}
      {validTransitions.length > 0 && canUpdateCellBatch(permissions) && (
        <Card className="p-6">
          <h3 className="mb-4 text-lg font-semibold">状态转换</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold">选择目标状态</label>
              <select
                value={selectedTransition}
                onChange={(e) => setSelectedTransition((e.target.value || '') as CellBatchStatus | '')}
                className="mt-2 w-full rounded border border-input bg-background px-3 py-2 text-sm"
                disabled={transitionLoading}
              >
                <option value="">-- 选择状态 --</option>
                {validTransitions.map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => setConfirmDialogOpen(true)}
              disabled={!selectedTransition || transitionLoading}
              className="w-full"
            >
              {transitionLoading ? '处理中…' : '确认转换'}
            </Button>
          </div>
        </Card>
      )}

      {/* Confirmation Dialog (simple version) */}
      {confirmDialogOpen && (
        <Card className="border-yellow-200 bg-yellow-50 p-6">
          <h4 className="font-semibold text-yellow-900">确认状态转换</h4>
          <p className="mt-2 text-sm text-yellow-800">
            您确定要将批次 {batch.batchNo} 从 <strong>{getStatusLabel(batch.status)}</strong> 转换到{' '}
            <strong>{getStatusLabel(selectedTransition)}</strong> 吗？
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleTransition}
              disabled={transitionLoading}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {transitionLoading ? '处理中…' : '确认'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmDialogOpen(false);
                setSelectedTransition('');
              }}
              disabled={transitionLoading}
            >
              取消
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    planned: '计划中',
    sample_collected: '样本已采集',
    sample_received: '样本已接收',
    processing: '处理中',
    qc_pending: '质检待审',
    released: '已放行',
    infusion_scheduled: '回输已排期',
    infused: '已回输',
    archived: '已归档',
    cancelled: '已取消',
    exception_hold: '异常暂停',
    qc_failed: '质检失败',
  };
  return labels[status] || status;
}
